import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface StatusBadgeProps {
  status: string;
}

const REPORT_STATUS: Record<string, { key: string; variant: BadgeVariant }> = {
  completed: { key: 'statusBadges.report.completed', variant: 'default' },
  processing: { key: 'statusBadges.report.processing', variant: 'secondary' },
  failed: { key: 'statusBadges.report.failed', variant: 'destructive' },
  pending: { key: 'statusBadges.report.pending', variant: 'outline' },
};

export function ReportStatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation('billing');
  const entry = REPORT_STATUS[status];
  if (!entry) return <Badge variant="outline">{status}</Badge>;
  return <Badge variant={entry.variant}>{t(entry.key)}</Badge>;
}

interface InvoiceStatusBadgeProps {
  status: string | null | undefined;
}

const INVOICE_STATUS: Record<string, { key: string; variant: BadgeVariant }> = {
  paid: { key: 'statusBadges.invoice.paid', variant: 'default' },
  open: { key: 'statusBadges.invoice.open', variant: 'secondary' },
  processing: { key: 'statusBadges.invoice.processing', variant: 'secondary' },
  past_due: { key: 'statusBadges.invoice.past_due', variant: 'destructive' },
  uncollectible: { key: 'statusBadges.invoice.uncollectible', variant: 'destructive' },
};

export function InvoiceStatusBadge({ status }: InvoiceStatusBadgeProps) {
  const { t } = useTranslation('billing');
  if (!status) return null;
  const entry = INVOICE_STATUS[status];
  if (!entry) return <Badge variant="outline">{status}</Badge>;
  return <Badge variant={entry.variant}>{t(entry.key)}</Badge>;
}

interface EnabledBadgeProps {
  enabled: boolean;
}

export function EnabledBadge({ enabled }: EnabledBadgeProps) {
  const { t } = useTranslation('billing');
  return (
    <Badge variant={enabled ? 'default' : 'secondary'}>
      {enabled ? t('statusBadges.enabled.active') : t('statusBadges.enabled.inactive')}
    </Badge>
  );
}
